import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Space, Select, Tag, Input, message, Row, Col, Typography, Modal, DatePicker, Button,
} from 'antd';
import {
  FileTextOutlined, SearchOutlined, SyncOutlined, FilterOutlined, EyeOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { OperationLogItem, getOperationLogs } from '../services/operationLogApi';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  cyan: '#1677ff',
  green: '#52c41a',
  orange: '#faad14',
  pink: '#ff4d4f',
  cardBg: '#ffffff',
  borderDark: '#f0f0f0',
};

const PageHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string; color?: string }> = ({ icon, title, subtitle, color = COLORS.primary }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
      <div style={{
        width: 48, height: 48,
        background: `${color}10`,
        borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24, color: color,
      }}>{icon}</div>
      <div>
        <Title level={2} style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{title}</Title>
        {subtitle && <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>{subtitle}</Text>}
      </div>
    </div>
    <div style={{
      width: 120, height: 3, marginLeft: 72,
      background: `linear-gradient(90deg, ${color}, ${color}66, transparent)`,
      borderRadius: 2,
    }} />
  </div>
);

const SimpleCard: React.FC<{ title?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties; accentColor?: string; noPadding?: boolean }> = ({ title, extra, children, style, accentColor = COLORS.primary, noPadding }) => (
  <Card
    title={title ? <Text style={{ fontWeight: 600, fontSize: 16 }}>{title}</Text> : undefined}
    extra={extra}
    style={{
      background: COLORS.cardBg,
      border: `1px solid ${COLORS.borderDark}`,
      borderRadius: 12,
      overflow: 'hidden',
      ...style,
    }}
    styles={{
      header: title ? {
        borderBottom: `1px solid ${COLORS.borderDark}`,
        background: `${accentColor}08`,
      } : undefined,
      body: { padding: noPadding ? 0 : 24 },
    }}
  >
    {children}
  </Card>
);

const getMethodColor = (method: string): string => {
  switch (method?.toUpperCase()) {
    case 'POST': return COLORS.cyan;
    case 'PUT': return COLORS.orange;
    case 'DELETE': return COLORS.pink;
    case 'GET': return COLORS.green;
    default: return '#6b7280';
  }
};

const getLoginTypeLabel = (loginType: string): string => {
  switch (loginType) {
    case 'jwt': return 'JWT';
    case 'dev-admin-token': return 'Dev Token';
    case 'secret-code': return 'Secret Code';
    case 'api-password': return 'API Password';
    default: return loginType || '-';
  }
};

const OperationLog: React.FC = () => {
  const [logs, setLogs] = useState<OperationLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 筛选条件
  const [searchUsername, setSearchUsername] = useState('');
  const [searchOperation, setSearchOperation] = useState('');
  const [filterStatus, setFilterStatus] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<[any, any] | null>(null);

  // 搜索触发器：只在 page/pageSize 变化或主动搜索时递增
  const [searchTrigger, setSearchTrigger] = useState(0);

  // 详情弹窗
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState<OperationLogItem | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, pageSize };
      if (searchUsername) params.username = searchUsername;
      if (searchOperation) params.operation = searchOperation;
      if (filterStatus !== null) params.status = filterStatus;
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startTime = dateRange[0].format('YYYY-MM-DD HH:mm:ss');
        params.endTime = dateRange[1].format('YYYY-MM-DD HH:mm:ss');
      }
      const result = await getOperationLogs(params);
      setLogs(result.list || []);
      setTotal(result.total || 0);
    } catch (e: any) {
      message.error('加载失败: ' + e.message);
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchTrigger]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = () => {
    if (page === 1) {
      setSearchTrigger(t => t + 1);
    } else {
      setPage(1);
    }
  };

  const handleReset = () => {
    setSearchUsername('');
    setSearchOperation('');
    setFilterStatus(null);
    setDateRange(null);
    if (page === 1) {
      setSearchTrigger(t => t + 1);
    } else {
      setPage(1);
    }
  };

  const showDetail = (record: OperationLogItem) => {
    setDetailRecord(record);
    setDetailVisible(true);
  };

  const formatParams = (params: string) => {
    if (!params) return '-';
    try {
      return JSON.stringify(JSON.parse(params), null, 2);
    } catch {
      return params;
    }
  };

  const columns: ColumnsType<OperationLogItem> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id: number) => (
        <Text style={{ color: '#6e7681', fontFamily: 'monospace' }}>#{id}</Text>
      ),
    },
    {
      title: '操作人',
      key: 'user',
      width: 120,
      render: (_: any, record: OperationLogItem) => (
        <div>
          <div><Text style={{ fontWeight: 500 }}>{record.userName || record.username || '-'}</Text></div>
          {record.username && record.userName && record.username !== record.userName && (
            <Text style={{ color: '#6e7681', fontSize: 12 }}>{record.username}</Text>
          )}
        </div>
      ),
    },
    {
      title: '登录方式',
      dataIndex: 'loginType',
      key: 'loginType',
      width: 100,
      render: (loginType: string) => (
        <Text style={{ color: '#6e7681', fontSize: 12 }}>{getLoginTypeLabel(loginType)}</Text>
      ),
    },
    {
      title: '操作描述',
      dataIndex: 'operation',
      key: 'operation',
      ellipsis: true,
      render: (operation: string) => (
        <Text style={{ fontWeight: 500 }}>{operation || '-'}</Text>
      ),
    },
    {
      title: '请求路径',
      dataIndex: 'requestUrl',
      key: 'requestUrl',
      width: 200,
      ellipsis: true,
      render: (url: string) => (
        <Text style={{ color: '#6e7681', fontSize: 12, fontFamily: 'monospace' }}>{url || '-'}</Text>
      ),
    },
    {
      title: '请求方式',
      dataIndex: 'requestMethod',
      key: 'requestMethod',
      width: 80,
      render: (method: string) => {
        const color = getMethodColor(method);
        return (
          <Tag style={{
            background: `${color}15`,
            border: `1px solid ${color}33`,
            color: color,
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 11,
          }}>
            {method}
          </Tag>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 70,
      render: (status: number) => {
        const isSuccess = status === 1;
        const color = isSuccess ? COLORS.green : COLORS.pink;
        return (
          <Tag style={{
            background: `${color}15`,
            border: `1px solid ${color}33`,
            color: color,
            borderRadius: 6,
          }}>
            {isSuccess ? '成功' : '失败'}
          </Tag>
        );
      },
    },
    {
      title: '耗时',
      dataIndex: 'executionTime',
      key: 'executionTime',
      width: 80,
      render: (time: number) => (
        <Text style={{ color: time > 1000 ? COLORS.warning : '#6e7681', fontSize: 12 }}>
          {time != null ? `${time}ms` : '-'}
        </Text>
      ),
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
      render: (ip: string) => (
        <Text style={{ color: '#6e7681', fontSize: 12, fontFamily: 'monospace' }}>{ip || '-'}</Text>
      ),
    },
    {
      title: '操作时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 160,
      render: (time: string) => (
        <Text style={{ color: '#6e7681', fontSize: 12 }}>
          {time ? new Date(time).toLocaleString('zh-CN') : '-'}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 60,
      render: (_: any, record: OperationLogItem) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => showDetail(record)}
          style={{ color: COLORS.cyan }}
          size="small"
        />
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <PageHeader
        icon={<FileTextOutlined />}
        title="操作日志"
        subtitle="记录管理员的操作行为，方便审计和问题追踪"
        color={COLORS.secondary}
      />

      {/* 筛选区域 */}
      <SimpleCard style={{ marginBottom: 24 }} accentColor={COLORS.secondary}>
        <Row gutter={[16, 16]} align="middle">
          <Col>
            <Space>
              <FilterOutlined style={{ color: COLORS.secondary }} />
              <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>筛选:</Text>
            </Space>
          </Col>
          <Col>
            <Input
              placeholder="用户名"
              value={searchUsername}
              onChange={(e) => setSearchUsername(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 140 }}
              prefix={<SearchOutlined style={{ color: '#6e7681' }} />}
              allowClear
            />
          </Col>
          <Col>
            <Input
              placeholder="操作描述"
              value={searchOperation}
              onChange={(e) => setSearchOperation(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 180 }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              value={filterStatus}
              onChange={(v) => setFilterStatus(v)}
              style={{ width: 100 }}
              allowClear
              placeholder="状态"
              options={[
                { value: null, label: '全部' },
                { value: 1, label: '成功' },
                { value: 0, label: '失败' },
              ]}
            />
          </Col>
          <Col>
            <RangePicker
              showTime
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [any, any] | null)}
              style={{ width: 360 }}
            />
          </Col>
          <Col flex={1} style={{ textAlign: 'right' }}>
            <Space>
              <Button
                onClick={handleSearch}
                icon={<SyncOutlined />}
                type="primary"
              >
                查询
              </Button>
              <Button onClick={handleReset} icon={<ReloadOutlined />}>
                重置
              </Button>
            </Space>
          </Col>
        </Row>
      </SimpleCard>

      {/* 日志列表 */}
      <SimpleCard
        title={
          <Space>
            <FileTextOutlined style={{ color: COLORS.secondary }} />
            <span>日志列表</span>
            <Tag style={{
              background: `${COLORS.secondary}15`,
              border: `1px solid ${COLORS.secondary}33`,
              color: COLORS.secondary,
            }}>
              {total}
            </Tag>
          </Space>
        }
        accentColor={COLORS.secondary}
        noPadding
      >
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (t) => <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>共 {t} 条</Text>,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ x: 1200 }}
          style={{ background: 'transparent' }}
          size="small"
        />
      </SimpleCard>

      {/* 详情弹窗 */}
      <Modal
        title={
          <Space>
            <EyeOutlined style={{ color: COLORS.secondary }} />
            <span>日志详情</span>
            {detailRecord && (
              <Text style={{ color: '#6e7681', fontSize: 13 }}>#{detailRecord.id}</Text>
            )}
          </Space>
        }
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={700}
      >
        {detailRecord && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>操作人: </Text>
                <Text strong>{detailRecord.userName || detailRecord.username || '-'}</Text>
              </Col>
              <Col span={12}>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>登录方式: </Text>
                <Text>{getLoginTypeLabel(detailRecord.loginType)}</Text>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>操作描述: </Text>
                <Text strong>{detailRecord.operation}</Text>
              </Col>
              <Col span={12}>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>方法: </Text>
                <Text code>{detailRecord.method}</Text>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>请求路径: </Text>
                <Text code>{detailRecord.requestUrl}</Text>
              </Col>
              <Col span={12}>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>请求方式: </Text>
                {(() => {
                  const color = getMethodColor(detailRecord.requestMethod);
                  return (
                    <Tag style={{ background: `${color}15`, border: `1px solid ${color}33`, color }}>
                      {detailRecord.requestMethod}
                    </Tag>
                  );
                })()}
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>状态: </Text>
                {(() => {
                  const isOk = detailRecord.status === 1;
                  const color = isOk ? COLORS.green : COLORS.pink;
                  return <Tag style={{ background: `${color}15`, border: `1px solid ${color}33`, color }}>{isOk ? '成功' : '失败'}</Tag>;
                })()}
              </Col>
              <Col span={8}>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>耗时: </Text>
                <Text>{detailRecord.executionTime != null ? `${detailRecord.executionTime}ms` : '-'}</Text>
              </Col>
              <Col span={8}>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>IP: </Text>
                <Text code>{detailRecord.ip || '-'}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>操作时间: </Text>
                <Text>{detailRecord.createTime ? new Date(detailRecord.createTime).toLocaleString('zh-CN') : '-'}</Text>
              </Col>
            </Row>

            {detailRecord.params && (
              <div>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>请求参数:</Text>
                <pre style={{
                  background: '#f6f8fa',
                  border: '1px solid #e1e4e8',
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 12,
                  maxHeight: 300,
                  overflow: 'auto',
                  marginTop: 4,
                }}>
                  {formatParams(detailRecord.params)}
                </pre>
              </div>
            )}

            {detailRecord.errorMsg && (
              <div>
                <Text style={{ color: COLORS.pink }}>错误信息:</Text>
                <pre style={{
                  background: '#fff2f0',
                  border: `1px solid ${COLORS.pink}33`,
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 12,
                  color: COLORS.pink,
                  maxHeight: 200,
                  overflow: 'auto',
                  marginTop: 4,
                }}>
                  {detailRecord.errorMsg}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OperationLog;
